import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LazyIcon } from "../../components/LazyIcon";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { useTowingEquipmentState } from "../../containers/TowingEquipmentContainer";
import { useUserPreferences } from "../../containers/UserPreferencesContainer";
import { Hangar, Ramp } from "../../types";

type Props = {
  locationType: string;
  isScheduledView?: boolean;
  location?: Hangar | Ramp;
  onLocationUpdate?: () => void;
};

export const ViewSettings: React.FC<Props> = ({
  locationType,
  isScheduledView = false,
  location,
  onLocationUpdate,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const { towingEquipments } = useTowingEquipmentState();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { preferences, setPreferences } = useUserPreferences();

  const isAutoParkEnabled = activeFBO?.settings?.enableAutoParking ?? false;

  const showCoPilot =
    locationType === "hangar" &&
    activeFBO?.subscription.includes("autostacking");

  const handleTugChange = async (evt: SelectChangeEvent<string>) => {
    const newTugId = evt.target.value || null;

    if (!location) {
      snackbar.notify({
        text: t('widgets.viewSettings.locationNotAvailable'),
        severity: "error",
      });
      return;
    }

    try {
      if (locationType === "hangar") {
        await postgrest
          .from("hangar")
          .update({ towing_equipment_id: newTugId })
          .eq("id", location.id);
      } else if (locationType === "ramp") {
        await postgrest
          .from("ramp")
          .update({ towing_equipment_id: newTugId })
          .eq("id", location.id);
      }

      snackbar.notify({
        text: t('widgets.viewSettings.tugUpdatedSuccess', { locationName: location.name }),
        severity: "success",
      });
      onLocationUpdate?.();
    } catch (error) {
      console.error("Failed to update tug:", error);
      snackbar.notify({
        text: t('widgets.viewSettings.tugUpdateError'),
        severity: "error",
      });
    }
  };

  const fboDefaultTug = towingEquipments?.find((t) => t.is_default);

  return (
    <>
      <Button
        variant="contained"
        color="info"
        onClick={handleClick}
        startIcon={<LazyIcon name="TuneIcon" />}
        sx={{
          "@media (max-width: 1300px)": { display: "none" },
        }}
      >
        {t('widgets.viewSettings.buttonText')}
      </Button>
      <Button
        color="info"
        variant="contained"
        onClick={handleClick}
        sx={{
          "@media (min-width: 1300px)": { display: "none" },
          mr: 1,
        }}
      >
        <LazyIcon name="TuneIcon" />
      </Button>
      <Popover
        data-testid="view_settings"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack direction="column" alignItems="flex-end" sx={{ p: 2 }}>
          <Tooltip title={t('widgets.viewSettings.tailNumberTooltip')} placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_tail_number"
                  color="primary"
                  checked={preferences.forceDisplayTailNumber ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      forceDisplayTailNumber:
                        !preferences.forceDisplayTailNumber,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.tailNumberLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip
            title={t('widgets.viewSettings.notesTooltip')}
            placement="right"
          >
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={preferences.notes ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      notes: !preferences.notes,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.notesLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip title={t('widgets.viewSettings.modelTooltip')} placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_model"
                  color="primary"
                  checked={preferences.forceDisplayModel ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      forceDisplayModel: !preferences.forceDisplayModel,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.modelLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip title={t('widgets.viewSettings.footprintTooltip')} placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_footprint"
                  color="primary"
                  checked={preferences.showMeasurements ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      showMeasurements: !preferences.showMeasurements,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.footprintLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip title={t('widgets.viewSettings.sopSpacingTooltip')} placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_sop_spacing"
                  color="primary"
                  checked={preferences.showSOPSpacing ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      showSOPSpacing: !preferences.showSOPSpacing,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.sopSpacingLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          {locationType === "hangar" && (
            <>
              <Tooltip title={t('widgets.viewSettings.wallSpacingTooltip')} placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_wall_spacing_fill"
                      color="primary"
                      checked={preferences.showWallSpacingFill ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showWallSpacingFill: !preferences.showWallSpacingFill,
                        })
                      }
                    />
                  }
                  label={t('widgets.viewSettings.wallSpacingLabel')}
                  labelPlacement="start"
                />
              </Tooltip>
              <Tooltip
                title={t('widgets.viewSettings.bayBoundariesTooltip')}
                placement="right"
              >
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_bay_boundaries"
                      color="primary"
                      checked={preferences.showBayBoundaries ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showBayBoundaries: !preferences.showBayBoundaries,
                        })
                      }
                    />
                  }
                  label={t('widgets.viewSettings.bayBoundariesLabel')}
                  labelPlacement="start"
                />
              </Tooltip>
            </>
          )}
          <Tooltip
            title={t('widgets.viewSettings.overlapsTooltip')}
            placement="right"
          >
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_overlap"
                  color="primary"
                  checked={preferences.showOverlaps ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      showOverlaps: !preferences.showOverlaps,
                    })
                  }
                />
              }
              label={t('widgets.viewSettings.overlapsLabel')}
              labelPlacement="start"
            />
          </Tooltip>
          {showCoPilot && (
            <Tooltip
              title={t('widgets.viewSettings.coPilotTooltip')}
              placement="right"
            >
              <FormControlLabel
                disabled={!activeFBO?.subscription.includes("autostacking")}
                control={
                  <Switch
                    data-testid="toggle_copilot"
                    color="primary"
                    checked={preferences.enableCoPilot ?? false}
                    onChange={() =>
                      setPreferences({
                        ...preferences,
                        enableCoPilot: !preferences.enableCoPilot,
                      })
                    }
                  />
                }
                label={t('widgets.viewSettings.coPilotLabel')}
                labelPlacement="start"
              />
            </Tooltip>
          )}
          {locationType === "hangar" &&
            location &&
            towingEquipments &&
            towingEquipments.length > 0 && (
              <TextField
                select
                label={t('widgets.viewSettings.tugLabel')}
                value={location.towing_equipment_id || ""}
                size="small"
                sx={{ minWidth: 200 }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  onChange: handleTugChange,
                  renderValue: (value): React.ReactNode => {
                    if (!value) {
                      return t('widgets.viewSettings.useFBODefault', { defaultTugName: fboDefaultTug?.name || t('widgets.viewSettings.none') });
                    }
                    const tug = towingEquipments.find(t => t.towing_equipment_id === value);
                    return (tug?.name || value) as React.ReactNode;
                  }
                }}
              >
                <MenuItem value="">
                  {t('widgets.viewSettings.useFBODefault', { defaultTugName: fboDefaultTug?.name || t('widgets.viewSettings.none') })}
                </MenuItem>
                {towingEquipments
                  .filter((t) => t.in_use)
                  .map((tug) => (
                    <MenuItem
                      key={tug.towing_equipment_id}
                      value={tug.towing_equipment_id}
                    >
                      {tug.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          {locationType === "ramp" && (
            <>
              <Tooltip title={t('widgets.viewSettings.spotsTooltip')} placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_spots"
                      color="primary"
                      checked={preferences.showSpots ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showSpots: !preferences.showSpots,
                        })
                      }
                    />
                  }
                  label={t('widgets.viewSettings.spotsLabel')}
                  labelPlacement="start"
                />
              </Tooltip>
              <Tooltip title={t('widgets.viewSettings.rampImageTooltip')} placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_ramp_reference_image"
                      color="primary"
                      checked={preferences.showRampReferenceImage ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showRampReferenceImage:
                            !preferences.showRampReferenceImage,
                        })
                      }
                    />
                  }
                  label={t('widgets.viewSettings.rampImageLabel')}
                  labelPlacement="start"
                />
              </Tooltip>
              {isScheduledView && isAutoParkEnabled && (
                <>
                  <Tooltip
                    title={t('widgets.viewSettings.zoneHeatmapTooltip', {
                      hours: preferences.heatmapHorizonHours || 72
                    })}
                    placement="right"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          data-testid="toggle_zone_heatmap"
                          color="primary"
                          checked={preferences.showZoneHeatmap ?? false}
                          onChange={() =>
                            setPreferences({
                              ...preferences,
                              showZoneHeatmap: !preferences.showZoneHeatmap,
                            })
                          }
                        />
                      }
                      label={t('widgets.viewSettings.zoneHeatmapLabel')}
                      labelPlacement="start"
                    />
                  </Tooltip>
                  {preferences.showZoneHeatmap && (
                    <Box sx={{ pl: 7, pb: 1 }}>
                      <ToggleButtonGroup
                        size="small"
                        value={preferences.heatmapHorizonHours || 72}
                        exclusive
                        onChange={(e, value) => {
                          if (value !== null) {
                            setPreferences({
                              ...preferences,
                              heatmapHorizonHours: value,
                            });
                          }
                        }}
                        sx={{ height: 28 }}
                      >
                        <ToggleButton value={24}>{t('widgets.viewSettings.hours24')}</ToggleButton>
                        <ToggleButton value={72}>{t('widgets.viewSettings.hours72')}</ToggleButton>
                        <ToggleButton value={168}>{t('widgets.viewSettings.week1')}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                </>
              )}
              {location && towingEquipments && towingEquipments.length > 0 && (
                <TextField
                  select
                  label={t('widgets.viewSettings.tugLabel')}
                  value={location.towing_equipment_id || ""}
                  size="small"
                  sx={{ minWidth: 200 }}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    onChange: handleTugChange,
                    renderValue: (value): React.ReactNode => {
                      if (!value) {
                        return t('widgets.viewSettings.useFBODefault', { defaultTugName: fboDefaultTug?.name || t('widgets.viewSettings.none') });
                      }
                      const tug = towingEquipments.find(t => t.towing_equipment_id === value);
                      return (tug?.name || value) as React.ReactNode;
                    }
                  }}
                >
                  <MenuItem value="">
                    {t('widgets.viewSettings.useFBODefault', { defaultTugName: fboDefaultTug?.name || t('widgets.viewSettings.none') })}
                  </MenuItem>
                  {towingEquipments
                    .filter((t) => t.in_use)
                    .map((tug) => (
                      <MenuItem
                        key={tug.towing_equipment_id}
                        value={tug.towing_equipment_id}
                      >
                        {tug.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
};
